import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users,
  UserCheck,
  Package,
  Calendar,
  CreditCard,
  CheckCircle,
  XCircle,
  Plus,
  Minus,
  AlertCircle,
  Loader2,
  RefreshCw,
  Clock,
  DollarSign,
  Shield,
  Zap,
  FileText,
  Bell,
  Settings,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useParadigm } from '../../contexts/ParadigmContext';
import AddSeatsModal from './AddSeatsModal';
import RemoveSeatsModal from './RemoveSeatsModal';
import SeatHistoryModal from './SeatHistoryModal';

const formatAmount = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Feature item component
const FeatureItem = ({ enabled, label }) => (
  <div className="flex items-center gap-2 text-sm">
    {enabled ? (
      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
    ) : (
      <XCircle className="w-4 h-4 text-gray-300 flex-shrink-0" />
    )}
    <span className={enabled ? 'text-gray-700' : 'text-gray-400'}>{label}</span>
  </div>
);

// Progress bar component
const ProgressBar = ({ used, total, color = 'blue' }) => {
  const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
  };

  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className={`h-2.5 rounded-full ${colorClasses[color] || colorClasses.blue}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Seat card component
const SeatCard = ({
  title,
  icon: Icon,
  data,
  onRemoveSeats,
  color = 'blue',
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-500',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      icon: 'text-purple-500',
    },
  };

  const colors = colorClasses[color] || colorClasses.blue;
  const removableSeats = data.purchased - (data.scheduled_removal || 0);
  const isUnlimited = data.total_available === null || data.limit_type === 'UNLIMITED';

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${colors.icon}`} />
          <h3 className={`font-semibold ${colors.text}`}>{title}</h3>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-baseline justify-between mb-1">
          {isUnlimited ? (
            <span className="text-2xl font-bold text-gray-900">
              {data.used} <span className="text-sm font-normal text-gray-500">used</span>
              <span className="ml-2 text-sm font-medium text-green-600">(Unlimited)</span>
            </span>
          ) : (
            <span className="text-2xl font-bold text-gray-900">
              {data.used} <span className="text-sm font-normal text-gray-500">of {data.total_available} used</span>
            </span>
          )}
        </div>
        {!isUnlimited && (
          <ProgressBar
            used={data.used}
            total={data.total_available}
            color={data.remaining === 0 ? 'red' : color}
          />
        )}
      </div>

      <div className="text-sm text-gray-600 space-y-1">
        {isUnlimited ? (
          <div className="flex justify-between">
            <span>Included in plan:</span>
            <span className="font-medium text-green-600">Unlimited</span>
          </div>
        ) : (
          <>
            <div className="flex justify-between">
              <span>Included in plan:</span>
              <span className="font-medium">{data.included}</span>
            </div>
            {data.custom > 0 && (
              <div className="flex justify-between">
                <span>Custom allocation:</span>
                <span className="font-medium">+{data.custom}</span>
              </div>
            )}
            {data.purchased > 0 && (
              <div className="flex justify-between">
                <span>Purchased:</span>
                <span className="font-medium">+{data.purchased}</span>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t border-gray-200">
              <span>Available:</span>
              <span className={`font-medium ${data.remaining === 0 ? 'text-red-600' : 'text-green-600'}`}>
                {data.remaining} seat{data.remaining !== 1 ? 's' : ''}
              </span>
            </div>
          </>
        )}
        {data.scheduled_removal > 0 && (
          <div className="flex justify-between text-orange-600">
            <span>Scheduled for removal:</span>
            <span className="font-medium">-{data.scheduled_removal}</span>
          </div>
        )}
      </div>

      {/* Only show remove button if there are removable seats */}
      {removableSeats > 0 && (
        <div className="mt-3">
          <button
            onClick={onRemoveSeats}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
          >
            <Minus className="w-4 h-4" />
            Remove Seats
          </button>
        </div>
      )}
    </div>
  );
};

const EntitlementsPage = ({ organizationId, currentUser, isAdmin = false }) => {
  const { labels } = useParadigm();
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [addSeatsModalOpen, setAddSeatsModalOpen] = useState(false);
  const [removeSeatsModal, setRemoveSeatsModal] = useState({ open: false, seatType: null });
  const [showHistory, setShowHistory] = useState(false);

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    features: true,
    limits: true,
  });

  const fetchEntitlements = useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBase()}/organizations/${organizationId}/entitlements`,
        { headers: getAuthHeaders() }
      );
      setEntitlements(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching entitlements:', err);
      setError(err.response?.data?.detail || 'Failed to load entitlements');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSeatsUpdated = () => {
    fetchEntitlements();
    setAddSeatsModalOpen(false);
    setRemoveSeatsModal({ open: false, seatType: null });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
        <button
          onClick={fetchEntitlements}
          className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }

  if (!entitlements?.subscription) {
    return (
      <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No Active Subscription</h3>
        <p className="text-gray-600 mb-2">
          {entitlements?.message || "This organization doesn't have an active subscription plan."}
        </p>
        {entitlements?.existing_subscription && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-left text-sm">
            <p className="font-medium text-yellow-800 mb-1">Existing Subscription Found:</p>
            <p className="text-yellow-700">
              Status: <span className="font-medium">{entitlements.existing_subscription.status}</span>
            </p>
            {entitlements.existing_subscription.package_name && (
              <p className="text-yellow-700">
                Package: <span className="font-medium">{entitlements.existing_subscription.package_name}</span>
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  const { subscription, seats, features, limits, available_components, package_details } = entitlements;

  return (
    <div className="bg-gray-50 min-h-screen space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription & Entitlements</h1>
          <p className="text-gray-600">{entitlements.organization_name}</p>
        </div>
        <button
          onClick={fetchEntitlements}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Subscription Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Current Plan Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-blue-500" />
            <h2 className="font-semibold text-gray-900">Current Plan</h2>
          </div>

          <div className="space-y-3">
            <div>
              <span className="text-2xl font-bold text-gray-900">{subscription.package_name}</span>
              {subscription.cancel_at_period_end && (
                <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                  Cancels at period end
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  subscription.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
              <span className="text-gray-500">|</span>
              <span className="text-sm text-gray-600">
                {subscription.billing_interval === 'yearly' ? 'Annual' : 'Monthly'} billing
              </span>
            </div>

            {package_details?.description && (
              <p className="text-sm text-gray-600">{package_details.description}</p>
            )}
          </div>
        </div>

        {/* Billing Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-green-500" />
            <h2 className="font-semibold text-gray-900">Billing</h2>
          </div>

          <div className="space-y-3">
            <div>
              <span className="text-2xl font-bold text-gray-900">
                {formatAmount(subscription.effective_price)}
              </span>
              <span className="text-gray-500">
                /{subscription.billing_interval === 'yearly' ? 'year' : 'month'}
              </span>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>
                  Current period: {formatDate(subscription.current_period_start)} -{' '}
                  {formatDate(subscription.current_period_end)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>Next billing: {formatDate(subscription.next_billing_date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <span>
                  Payment:{' '}
                  {subscription.payment_type === 'credit_card' ? 'Credit Card' : 'Invoice'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seats Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            <h2 className="font-semibold text-gray-900">Seats</h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Show Add Seats button only if at least one category can add seats */}
            {((seats.executives.total_available !== null && seats.executives.can_add_more) ||
              (seats.eas.total_available !== null && seats.eas.can_add_more)) && (
              <button
                onClick={() => setAddSeatsModalOpen(true)}
                className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Seats
              </button>
            )}
            <button
              onClick={() => setShowHistory(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <FileText className="w-4 h-4" />
              View History
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SeatCard
            title={labels.executives}
            icon={UserCheck}
            data={seats.executives}
            color="blue"
            onRemoveSeats={() => setRemoveSeatsModal({ open: true, seatType: 'executive' })}
          />
          <SeatCard
            title={labels.executiveAssistants}
            icon={Users}
            data={seats.eas}
            color="purple"
            onRemoveSeats={() => setRemoveSeatsModal({ open: true, seatType: 'ea' })}
          />
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('features')}
          className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            <h2 className="font-semibold text-gray-900">Features Included</h2>
          </div>
          {expandedSections.features ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSections.features && (
          <div className="px-5 pb-5 border-t border-gray-100">
            {(() => {
              const featureList = [
                { key: 'teams_enabled', label: 'Team Collaboration' },
                { key: 'flight_verification_enabled', label: 'Flight Verification' },
                { key: 'sso_enabled', label: 'SSO' },
                { key: 'saml_sso_enabled', label: 'SAML SSO' },
                { key: 'oidc_sso_enabled', label: 'OIDC SSO' },
                { key: 'audit_logs_enabled', label: 'Audit Logs' },
                { key: 'rbac_enabled', label: 'Role-Based Access' },
                { key: 'sticky_notes_enabled', label: 'Sticky Notes' },
                { key: 'todos_enabled', label: 'Tasks/Todos' },
                { key: 'dedicated_emails_executives', label: labels.dedicatedEmailsExecutives },
                { key: 'dedicated_emails_eas', label: labels.dedicatedEmailsEas },
              ];
              const enabledFeatures = featureList.filter((f) => features[f.key]);

              if (enabledFeatures.length === 0) {
                return (
                  <p className="text-sm text-gray-500 pt-4">No additional features included in this plan.</p>
                );
              }

              return (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-4">
                  {enabledFeatures.map((f) => (
                    <FeatureItem key={f.key} enabled={true} label={f.label} />
                  ))}
                </div>
              );
            })()}

            {available_components?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Available Components</h3>
                <div className="flex flex-wrap gap-2">
                  {available_components.map((component) => (
                    <span
                      key={component}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                    >
                      {component.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usage Limits Section */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('limits')}
          className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-500" />
            <h2 className="font-semibold text-gray-900">Usage Limits (This Period)</h2>
          </div>
          {expandedSections.limits ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSections.limits && (
          <div className="px-5 pb-5 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              {/* Flight Alerts */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">Flight Alerts</span>
                  {limits.flight_alerts.per_executive && (
                    <span className="text-xs text-gray-500">{labels.perExecutive}</span>
                  )}
                </div>
                {limits.flight_alerts.enabled ? (
                  <>
                    <div className="text-lg font-semibold text-gray-900 mb-1">
                      {limits.flight_alerts.used} / {limits.flight_alerts.limit || 'Unlimited'}
                    </div>
                    {limits.flight_alerts.limit && (
                      <ProgressBar
                        used={limits.flight_alerts.used}
                        total={limits.flight_alerts.limit}
                        color="purple"
                      />
                    )}
                  </>
                ) : (
                  <span className="text-gray-400">Not enabled</span>
                )}
              </div>

              {/* Document Parsing */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">Document Parsing</span>
                  {limits.document_parsing.per_executive && (
                    <span className="text-xs text-gray-500">{labels.perExecutive}</span>
                  )}
                </div>
                {limits.document_parsing.enabled ? (
                  <>
                    <div className="text-lg font-semibold text-gray-900 mb-1">
                      {limits.document_parsing.used} / {limits.document_parsing.limit || 'Unlimited'}
                    </div>
                    {limits.document_parsing.limit && (
                      <ProgressBar
                        used={limits.document_parsing.used}
                        total={limits.document_parsing.limit}
                        color="blue"
                      />
                    )}
                  </>
                ) : (
                  <span className="text-gray-400">Not enabled</span>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Usage resets at the start of each billing period ({formatDate(subscription.current_period_end)})
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {addSeatsModalOpen && (
        <AddSeatsModal
          organizationId={organizationId}
          executiveSeats={seats.executives}
          eaSeats={seats.eas}
          onClose={() => setAddSeatsModalOpen(false)}
          onSuccess={handleSeatsUpdated}
        />
      )}

      {removeSeatsModal.open && (
        <RemoveSeatsModal
          organizationId={organizationId}
          seatType={removeSeatsModal.seatType}
          currentSeats={
            removeSeatsModal.seatType === 'executive'
              ? seats.executives
              : seats.eas
          }
          periodEnd={subscription.current_period_end}
          onClose={() => setRemoveSeatsModal({ open: false, seatType: null })}
          onSuccess={handleSeatsUpdated}
        />
      )}

      {showHistory && (
        <SeatHistoryModal
          organizationId={organizationId}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

export default EntitlementsPage;
